import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/shared/lib/utils'
import { lockBodyScroll, unlockBodyScroll } from '@/shared/lib/scroll-lock'
import { XIcon } from '@/shared/components/icons'

const ModalFooterSlot = createContext<HTMLDivElement | null>(null)

// Renders its children into the modal's fixed footer bar, so action buttons stay
// pinned to the bottom edge instead of scrolling with the form.
export function ModalFooter({ children }: { children: ReactNode }) {
  const slot = useContext(ModalFooterSlot)
  if (!slot) {
    return <div className="mt-4 border-t border-line pt-4">{children}</div>
  }
  return createPortal(children, slot)
}

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  className?: string
  // When false, the backdrop and Escape no longer dismiss. The close control
  // stays so keyboard users are never trapped.
  dismissible?: boolean
}

const focusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  className,
  dismissible = true,
}: ModalProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const [footerNode, setFooterNode] = useState<HTMLDivElement | null>(null)

  // Latest handlers live in refs so the focus effect never re-runs when the
  // parent re-renders. Re-running it would steal focus on every keystroke.
  const onCloseRef = useRef(onClose)
  const dismissibleRef = useRef(dismissible)
  onCloseRef.current = onClose
  dismissibleRef.current = dismissible

  useEffect(() => {
    if (!open) return

    restoreFocusRef.current = document.activeElement as HTMLElement | null
    lockBodyScroll()

    const panel = panelRef.current
    const firstInBody = bodyRef.current?.querySelector<HTMLElement>(focusableSelector)
    ;(firstInBody ?? panel)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        if (dismissibleRef.current) onCloseRef.current()
        return
      }

      if (event.key !== 'Tab' || !panel) return

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (node) => node.offsetParent !== null
      )
      if (focusable.length === 0) return

      const firstNode = focusable[0]
      const lastNode = focusable[focusable.length - 1]
      if (!firstNode || !lastNode) return

      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault()
        lastNode.focus()
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault()
        firstNode.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      unlockBodyScroll()
      restoreFocusRef.current?.focus()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-60 flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-navy-950/60 backdrop-blur-sm"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-raised sm:max-w-lg sm:rounded-3xl',
          className
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 px-5 pb-4 pt-5 md:px-6 md:pt-6">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-navy-900">
              {title}
            </h2>
            {description && <p className="mt-1 text-sm text-neutral-600">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-navy-900"
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div
          ref={bodyRef}
          className="scroll-slim min-h-0 flex-1 overflow-y-auto px-5 pb-5 md:px-6 md:pb-6"
        >
          <ModalFooterSlot.Provider value={footerNode}>{children}</ModalFooterSlot.Provider>
        </div>

        <div
          ref={setFooterNode}
          className="shrink-0 border-t border-line bg-white px-5 py-4 empty:hidden md:px-6"
        />
      </div>
    </div>,
    document.body
  )
}
